import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';

import { useEditCompanyPestModalStore } from './hooks/EditCompanyPestModalStore';

import { MLInput } from 'components/Input/Input';
import { MLSelect } from 'components/Select/Select';
import { clientWithPestCompanyEdit, ClientWithPestCompanyEditValues } from 'api/clients/types';
import MLTextArea from 'components/Input/TextArea';
import { countries } from 'api/clients/country-list';
import { checkIpClient } from 'api/clients/checkIpClient';
import useEditPestCompany from 'hooks/Clients/useEditPestCompany';
import { getInfoPestCompany } from 'api/clients/getInfoPestCompany';

function EditPestCompanyModal() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { editPestCompany } = useEditPestCompany();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onManageEditClose } = useEditCompanyPestModalStore();
  const [companyId, setCompanyId] = useState<number | null>(null);
  const { handleSubmit, control, setValue, reset, setError } = useForm<ClientWithPestCompanyEditValues>({
    resolver: zodResolver(clientWithPestCompanyEdit),
    defaultValues: {
      name: '',
      EDRPOU: '',
      ownership_form: '',
      country: '',
      city_text: '',
      zip_code: '',
      street: '',
      building_no: '',
      apartment_no: '',
      logo: undefined,
    },
  });
  useEffect(() => {
    if (isOpen) {
      const fetchProfileData = async () => {
        setIsLoading(true);
        try {
          const profileData = await getInfoPestCompany();

          setCompanyId(profileData.id);

          let countryId = '';
          if (profileData.country) {
            const foundCountry = countries.find(c => c.countryName === profileData.country);
            if (foundCountry) {
              countryId = String(foundCountry.id);
            }
          }

          reset({
            name: profileData.name || '',
            EDRPOU: profileData.EDRPOU || '',
            ownership_form: profileData.ownership_form || '',
            country: countryId || '',
            city_text: profileData.city_text || '',
            zip_code: profileData.zip_code || '',
            street: profileData.street || '',
            building_no: profileData.building_no || '',
            apartment_no: profileData.apartment_no || '',
            description: profileData.description || '',
          });
        } catch (error) {
          console.error('Error fetching company info:', error);
          enqueueSnackbar(t('ClientsDashboard.dashboard.error'), { variant: 'error' });
        } finally {
          setIsLoading(false);
        }
      };

      fetchProfileData();
    }
  }, [isOpen, reset, t]);
  const handlerError = (error: any) => {
    if (error.response && error.response.data) {
      const data = error.response.data;
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          setError(key as keyof ClientWithPestCompanyEditValues, { message: data[key][0] });
        }
      }
    } else {
      enqueueSnackbar(t('ClientsDashboard.dashboard.error'), { variant: 'error' });
    }
  };

  const countryList = useMemo(() => {
    if (countries.length === 0 || !countries) {
      return [{ value: '', label: "Don't have countries" }];
    }
    return [{ value: '', label: 'Select Country' }].concat(
      countries.map(country => {
        return {
          value: String(country.id),
          label: country.countryName,
          flagImg: country.flagImg,
        };
      }),
    );
  }, []);

  const handlerEditPestCompany = async (data: ClientWithPestCompanyEditValues) => {
    if (!companyId) {
      enqueueSnackbar(t('ClientsDashboard.dashboard.error'), { variant: 'error' });
      return;
    }

    const clientData: ClientWithPestCompanyEditValues = {
      id: companyId,
      name: data.name,
      ownership_form: data.ownership_form,
      description: data.description,
      logo: data.logo,
      EDRPOU: data.EDRPOU || '',
      zip_code: data.zip_code || '',
      country: data.country,
      city_text: data.city_text,
      street: data.street,
      building_no: data.building_no,
      apartment_no: data.apartment_no || '',
    };

    editPestCompany(
      { client: clientData },
      {
        onSuccess: () => {
          onManageEditClose();
          enqueueSnackbar(t('ClientsDashboard.success'), { variant: 'success' });
          queryClient.invalidateQueries(['pest-company']);
        },
        onError: (error: any) => {
          handlerError(error);
          enqueueSnackbar(t('ClientsDashboard.dashboard.error'), { variant: 'error' });
          console.error('Error updating client:', error);
        },
      },
    );
  };

  useEffect(() => {
    const checkServiceProvider = async () => {
      try {
        const getIpClient = await checkIpClient();
        if (getIpClient && getIpClient.country_name) {
          const foundCountry = countries.find(c => c.countryName === getIpClient.country_name);

          if (foundCountry) {
            setValue('country', String(foundCountry.id));
          }
        }
      } catch (error) {
        console.error('Error fetching service provider status or payed_till:', error);
      }
    };

    checkServiceProvider();
  }, [setValue]);

  const formatCountryOption = (option: any) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {option.flagImg && (
        <img src={option.flagImg} alt={option.label} style={{ width: '20px', height: '15px', display: 'block' }} />
      )}
      <span>{option.label}</span>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={onManageEditClose} size="xl" backdrop="static" keyboard={false}>
      <ModalHeader className="border-bottom py-3" toggle={onManageEditClose}>
        <div className="text-purple">{t('Modals.editPestCompanyTitle')}</div>
      </ModalHeader>

      <ModalBody className="py-3">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner color="primary" />
          </div>
        ) : (
          <form className="row g-3 mt-2">
            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    placeholder="Trojan System"
                    label={t('Modals.manage-client.name')}
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="EDRPOU"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    type="text"
                    placeholder="113442121"
                    label={t('Modals.manage-client.EDRPOU')}
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="ownership_form"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    type="text"
                    placeholder="owenership form"
                    label={t('Modals.manage-client.ownership_form')}
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="country"
                render={({ field, fieldState: { error } }) => (
                  <MLSelect
                    {...field}
                    label={t('Modals.manage-client.country')}
                    error={error?.message}
                    options={countryList}
                    formatOptionLabel={formatCountryOption}
                    value={String(field.value || '')}
                    onChange={val => field.onChange(val || '')}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="city_text"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    type="text"
                    label={t('Modals.manage-client.city')}
                    placeholder="New York"
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="zip_code"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    type="text"
                    label={t('Modals.manage-client.zip')}
                    placeholder="10001"
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="street"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    type="text"
                    label={t('Modals.manage-client.street')}
                    placeholder="Houston"
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="building_no"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    type="text"
                    label={t('Modals.manage-client.building_no')}
                    placeholder="0"
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                control={control}
                name="apartment_no"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    type="text"
                    label={t('Modals.manage-client.apartment_no')}
                    placeholder="12"
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="col-12">
              <h6 className="mb-2">{t('Modals.manage-client.description')}</h6>
              <Controller
                control={control}
                name="description"
                render={({ field, fieldState: { error } }) => (
                  <MLTextArea style={{ width: '100%' }} error={error?.message} {...field} />
                )}
              />
            </div>

            <div className="col-12">
              <Controller
                control={control}
                name="logo"
                render={({ field: { value, onChange, ...fieldProps }, fieldState: { error } }) => (
                  <MLInput
                    {...fieldProps}
                    type="file"
                    label={t('Modals.manage-client.logo')}
                    placeholder="Logo"
                    error={error?.message}
                    value={undefined}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      onChange(file);
                    }}
                  />
                )}
              />
            </div>
          </form>
        )}
      </ModalBody>

      <ModalFooter>
        <Button className="btn btn-ghost-danger" onClick={onManageEditClose}>
          {t('Modals.delete-modal.cancel')}
        </Button>
        <Button className="btn btn-ghost-success" onClick={handleSubmit(handlerEditPestCompany)}>
          {t('Modals.manage-client.edit-btn')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default EditPestCompanyModal;
